/**
 * Персистентность pending-попыток для /clients/states: ОДНА "create" на
 * tenant+admin+key и ОДНА "patch" на tenant+admin+definitionId, в localStorage
 * (переживает unmount/logout/закрытие вкладки — симметрично
 * scenarioCreateAttemptStorage.ts/scenarioEditAttemptStorage.ts, которые эти
 * паттерны переиспользуют почти буквально: именованный navigator.locks на
 * claim/clear, маркер = замороженное тело + identity + версия/ключ).
 *
 * НИКОГДА не хранит DTO сценариев/финансовую логику — тело здесь предельно
 * простое ({key,label,policies,is_active} или {expected_version,label?,
 * is_active?}), поэтому формат маркера свой, не импортированный из marketing/.
 */

export const STATE_ATTEMPT_SCHEMA = 1 as const;

export interface StoredCreateDefinitionBody {
  key: string;
  label: string;
  policies: Record<string, { ttl_seconds: number | null; next_state: string | null }>;
  is_active: boolean;
}

export interface StoredPatchDefinitionBody {
  expected_version: number;
  label?: string;
  is_active?: boolean;
}

export interface StoredCreateMarker {
  schema: typeof STATE_ATTEMPT_SCHEMA;
  kind: 'create';
  tenantId: number;
  adminId: number;
  key: string;
  bodyJson: string;
}

export interface StoredPatchMarker {
  schema: typeof STATE_ATTEMPT_SCHEMA;
  kind: 'patch';
  tenantId: number;
  adminId: number;
  definitionId: number;
  expectedVersion: number;
  bodyJson: string;
}

export type StoredRead<M> =
  | { status: 'empty' }
  | { status: 'valid'; marker: M }
  | { status: 'corrupt' }
  | { status: 'storage_error' };

const NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
const MAX_BODY_JSON_CHARS = 65536;

function isPositiveSafeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidPolicyEntry(value: unknown): boolean {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 2 || !keys.includes('ttl_seconds') || !keys.includes('next_state')) return false;
  const ttl = value.ttl_seconds;
  const next = value.next_state;
  const ttlOk = ttl === null || (isPositiveSafeInt(ttl) && ttl <= 31536000);
  const nextOk = next === null || (typeof next === 'string' && NAME_RE.test(next));
  if ((ttl === null) !== (next === null)) return false;
  return ttlOk && nextOk;
}

function isValidCreateBody(body: unknown): body is StoredCreateDefinitionBody {
  if (!isPlainObject(body)) return false;
  if (Object.keys(body).length !== 4) return false;
  if (typeof body.key !== 'string' || !NAME_RE.test(body.key)) return false;
  if (typeof body.label !== 'string' || body.label.length < 1 || body.label.length > 255) return false;
  if (typeof body.is_active !== 'boolean') return false;
  if (!isPlainObject(body.policies)) return false;
  const stateNames = Object.keys(body.policies);
  if (stateNames.length < 1 || stateNames.length > 50) return false;
  if (!stateNames.every((name) => NAME_RE.test(name) && isValidPolicyEntry((body.policies as Record<string, unknown>)[name]))) {
    return false;
  }
  return true;
}

function isValidPatchBody(body: unknown): body is StoredPatchDefinitionBody {
  if (!isPlainObject(body)) return false;
  const keys = Object.keys(body);
  if (keys.some((k) => k !== 'expected_version' && k !== 'label' && k !== 'is_active')) return false;
  if (!isPositiveSafeInt(body.expected_version)) return false;
  if ('label' in body && (typeof body.label !== 'string' || body.label.length < 1 || body.label.length > 255)) return false;
  if ('is_active' in body && typeof body.is_active !== 'boolean') return false;
  return true;
}

export function createLockName(tenantId: number, adminId: number, key: string): string {
  return `state-def-create:${tenantId}:${adminId}:${key}`;
}
export function createStorageKey(tenantId: number, adminId: number, key: string): string {
  return `state_def_create:${tenantId}:${adminId}:${key}`;
}
export function patchLockName(tenantId: number, adminId: number, definitionId: number): string {
  return `state-def-patch:${tenantId}:${adminId}:${definitionId}`;
}
export function patchStorageKey(tenantId: number, adminId: number, definitionId: number): string {
  return `state_def_patch:${tenantId}:${adminId}:${definitionId}`;
}

function isValidCreateMarkerShape(value: unknown, tenantId: number, adminId: number, key: string): value is StoredCreateMarker {
  if (!isPlainObject(value)) return false;
  const r = value as Record<string, unknown>;
  if (Object.keys(r).length !== 6) return false;
  if (r.schema !== STATE_ATTEMPT_SCHEMA || r.kind !== 'create') return false;
  if (r.tenantId !== tenantId || r.adminId !== adminId || r.key !== key) return false;
  if (typeof r.bodyJson !== 'string' || r.bodyJson.length === 0 || r.bodyJson.length > MAX_BODY_JSON_CHARS) return false;
  let parsed: unknown;
  try { parsed = JSON.parse(r.bodyJson); } catch { return false; }
  return isValidCreateBody(parsed);
}

function isValidPatchMarkerShape(
  value: unknown, tenantId: number, adminId: number, definitionId: number,
): value is StoredPatchMarker {
  if (!isPlainObject(value)) return false;
  const r = value as Record<string, unknown>;
  if (Object.keys(r).length !== 7) return false;
  if (r.schema !== STATE_ATTEMPT_SCHEMA || r.kind !== 'patch') return false;
  if (r.tenantId !== tenantId || r.adminId !== adminId || r.definitionId !== definitionId) return false;
  if (!isPositiveSafeInt(r.expectedVersion)) return false;
  if (typeof r.bodyJson !== 'string' || r.bodyJson.length === 0 || r.bodyJson.length > MAX_BODY_JSON_CHARS) return false;
  let parsed: unknown;
  try { parsed = JSON.parse(r.bodyJson); } catch { return false; }
  if (!isValidPatchBody(parsed)) return false;
  return (parsed as StoredPatchDefinitionBody).expected_version === r.expectedVersion;
}

export function readStoredCreateAttempt(tenantId: number, adminId: number, key: string): StoredRead<StoredCreateMarker> {
  let raw: string | null;
  try { raw = window.localStorage.getItem(createStorageKey(tenantId, adminId, key)); } catch { return { status: 'storage_error' }; }
  if (raw === null) return { status: 'empty' };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'corrupt' }; }
  if (!isValidCreateMarkerShape(parsed, tenantId, adminId, key)) return { status: 'corrupt' };
  return { status: 'valid', marker: parsed };
}

export function readStoredPatchAttempt(
  tenantId: number, adminId: number, definitionId: number,
): StoredRead<StoredPatchMarker> {
  let raw: string | null;
  try { raw = window.localStorage.getItem(patchStorageKey(tenantId, adminId, definitionId)); } catch { return { status: 'storage_error' }; }
  if (raw === null) return { status: 'empty' };
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'corrupt' }; }
  if (!isValidPatchMarkerShape(parsed, tenantId, adminId, definitionId)) return { status: 'corrupt' };
  return { status: 'valid', marker: parsed };
}

export function writeStoredCreateAttempt(marker: StoredCreateMarker): boolean {
  try {
    window.localStorage.setItem(createStorageKey(marker.tenantId, marker.adminId, marker.key), JSON.stringify(marker));
    return true;
  } catch { return false; }
}
export function writeStoredPatchAttempt(marker: StoredPatchMarker): boolean {
  try {
    window.localStorage.setItem(patchStorageKey(marker.tenantId, marker.adminId, marker.definitionId), JSON.stringify(marker));
    return true;
  } catch { return false; }
}
export function removeStoredCreateAttempt(tenantId: number, adminId: number, key: string): boolean {
  try { window.localStorage.removeItem(createStorageKey(tenantId, adminId, key)); return true; } catch { return false; }
}
export function removeStoredPatchAttempt(tenantId: number, adminId: number, definitionId: number): boolean {
  try { window.localStorage.removeItem(patchStorageKey(tenantId, adminId, definitionId)); return true; } catch { return false; }
}

export function createMarkersMatch(a: StoredCreateMarker, b: StoredCreateMarker): boolean {
  return a.schema === b.schema && a.tenantId === b.tenantId && a.adminId === b.adminId
    && a.key === b.key && a.bodyJson === b.bodyJson;
}
export function patchMarkersMatch(a: StoredPatchMarker, b: StoredPatchMarker): boolean {
  return a.schema === b.schema && a.tenantId === b.tenantId && a.adminId === b.adminId
    && a.definitionId === b.definitionId && a.expectedVersion === b.expectedVersion && a.bodyJson === b.bodyJson;
}

export function buildCreateMarker(
  tenantId: number, adminId: number, key: string, bodyJson: string,
): StoredCreateMarker {
  return { schema: STATE_ATTEMPT_SCHEMA, kind: 'create', tenantId, adminId, key, bodyJson };
}
export function buildPatchMarker(
  tenantId: number, adminId: number, definitionId: number, expectedVersion: number, bodyJson: string,
): StoredPatchMarker {
  return { schema: STATE_ATTEMPT_SCHEMA, kind: 'patch', tenantId, adminId, definitionId, expectedVersion, bodyJson };
}

export function isWebLocksAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.locks) && typeof navigator.locks.request === 'function';
}

export const STATE_ATTEMPT_EVENT = 'client-state-definition-attempt-changed';

export function notifyStateAttemptChanged(detail: Record<string, unknown>): void {
  try { window.dispatchEvent(new CustomEvent(STATE_ATTEMPT_EVENT, { detail })); } catch { /* display-only */ }
}
